import React, { useState, useRef } from 'react';
import { DesktopIcon as DesktopIconType } from '../hooks/useOS';
import { ContextMenu, useContextMenu, ContextMenuItem } from './ui/context-menu-custom';
import { Settings } from 'lucide-react';

const DESKTOP_ICON_SIZE = 48;
const DESKTOP_ICON_WIDTH = 80;
const DESKTOP_ICON_HEIGHT = 88;

interface DesktopIconProps {
  icon: DesktopIconType;
  onDoubleClick: () => void;
  onMove: (x: number, y: number) => void;
  isSelected: boolean;
  onSelect: () => void;
  onOpenWindow?: (appId: string, title: string) => void;
  onRename?: (iconId: string, newName: string) => void;
  onFinishEditing?: (iconId: string) => void;
  onDelete?: (iconId: string) => void;
  onEmptyRecycleBin?: () => void;
  canEmptyRecycleBin?: boolean;
}

export const DesktopIcon: React.FC<DesktopIconProps> = ({ 
  icon, 
  onDoubleClick, 
  onMove, 
  isSelected, 
  onSelect, 
  onOpenWindow, 
  onRename, 
  onFinishEditing,
  onDelete,
  onEmptyRecycleBin,
  canEmptyRecycleBin = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: icon.x, y: icon.y });
  const [editingName, setEditingName] = useState(icon.label);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [tapCount, setTapCount] = useState(0);
  const iconRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { contextMenu, showContextMenu, hideContextMenu } = useContextMenu();

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    onSelect();
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const currentTime = Date.now();
      const timeSinceLastTap = currentTime - lastTapTime;
      
      // Check for double tap (within 300ms)
      if (timeSinceLastTap < 300 && timeSinceLastTap > 50) {
        // Double tap detected - open the icon
        e.preventDefault();
        e.stopPropagation();
        onDoubleClick();
        setTapCount(0);
        setLastTapTime(0);
        return;
      }
      
      // Single tap or first tap of potential double tap
      setLastTapTime(currentTime);
      setTapCount(1);
      onSelect();
      
      // Store touch info for potential drag operation
      const touch = e.touches[0];
      const touchDragOffset = {
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      };
      
      // Start drag operation after a short delay if no second tap comes
      setTimeout(() => {
        const now = Date.now();
        if (now - lastTapTime >= 250 && tapCount === 1) {
          // No second tap detected - start dragging
          setIsDragging(true);
          setDragOffset(touchDragOffset);
          setTapCount(0);
        }
      }, 250);
      
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - DESKTOP_ICON_WIDTH));
      const newY = Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - DESKTOP_ICON_HEIGHT));
      setPosition({ x: newX, y: newY });
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault(); // Prevent scrolling
      const touch = e.touches[0];
      const newX = Math.max(0, Math.min(touch.clientX - dragOffset.x, window.innerWidth - DESKTOP_ICON_WIDTH));
      const newY = Math.max(0, Math.min(touch.clientY - dragOffset.y, window.innerHeight - DESKTOP_ICON_HEIGHT));
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      onMove(position.x, position.y);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      onMove(position.x, position.y);
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (icon.id === 'recycle-bin') {
      const contextItems: ContextMenuItem[] = [
        {
          id: 'open',
          label: 'Open',
          onClick: () => {
            onDoubleClick();
          }
        },
        {
          id: 'separator1',
          label: '',
          separator: true
        },
        {
          id: 'empty-recycle-bin',
          label: 'Empty Recycle Bin',
          onClick: () => {
            onEmptyRecycleBin?.();
          },
          disabled: !canEmptyRecycleBin
        }
      ];

      showContextMenu(e, contextItems);
      return;
    }

    // Special context menu for "My Computer" icon
    if (icon.id === 'my-computer') {
      const contextItems: ContextMenuItem[] = [
        {
          id: 'open',
          label: 'Open',
          onClick: () => {
            onDoubleClick();
          }
        },
        {
          id: 'separator1',
          label: '',
          separator: true
        },
        {
          id: 'properties',
          label: 'Properties',
          icon: <Settings size={16} />,
          onClick: () => {
            if (onOpenWindow) {
              onOpenWindow('system-properties', 'System Properties');
            }
          }
        }
      ];
      
      showContextMenu(e, contextItems);
    } else {
      // Context menu for file/folder icons
      const contextItems: ContextMenuItem[] = [
        {
          id: 'open',
          label: 'Open',
          onClick: () => {
            onDoubleClick();
          }
        }
      ];

      // Add rename and delete for file/folder icons (not system icons)
      if (icon.filePath && icon.filePath.startsWith('C:\\User\\Vince\\Desktop\\')) {
        contextItems.push(
          {
            id: 'separator1',
            label: '',
            separator: true
          },
          {
            id: 'rename',
            label: 'Rename',
            onClick: () => {
              // Set the icon to editing mode
              hideContextMenu();
              // This will trigger the editing state via the parent component
              if (onRename) {
                // We'll need to modify this to work with the desktop's icon editing system
                onRename(icon.id, icon.label); // This will set isEditing to true
              }
            }
          },
          {
            id: 'delete',
            label: 'Delete',
            onClick: () => {
              if (onDelete) {
                onDelete(icon.id);
              }
            }
          }
        );
      }
      
      showContextMenu(e, contextItems);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishRename();
    } else if (e.key === 'Escape') {
      cancelRename();
    }
  };

  const handleRenameBlur = () => {
    finishRename();
  };

  const finishRename = () => {
    if (onRename && editingName.trim() !== icon.label && editingName.trim()) {
      onRename(icon.id, editingName.trim());
    }
    if (onFinishEditing) {
      onFinishEditing(icon.id);
    }
  };

  const cancelRename = () => {
    setEditingName(icon.label);
    if (onFinishEditing) {
      onFinishEditing(icon.id);
    }
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, position, dragOffset]);

  React.useEffect(() => {
    setPosition({ x: icon.x, y: icon.y });
  }, [icon.x, icon.y]);

  // Auto-focus input when editing starts
  React.useEffect(() => {
    if (icon.isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [icon.isEditing]);

  // Update editing name when icon label changes
  React.useEffect(() => {
    setEditingName(icon.label);
  }, [icon.label]);

  const IconComponent = icon.icon;
  const selectedLabelClasses = 'bg-white/90 text-slate-900 ring-1 ring-slate-500/60 shadow-sm dark:bg-slate-900/70 dark:text-slate-100 dark:ring-white/20';
  const renameInputClasses = 'text-xs text-slate-900 text-center leading-tight px-2 py-1 rounded-md bg-white/95 border border-slate-500/70 outline-none shadow-sm w-24 dark:bg-slate-900/85 dark:text-slate-100 dark:border-white/20';

  return (
    <>
      <div
        ref={iconRef}
        className="absolute flex flex-col items-center cursor-pointer w-20 select-none"
        style={{
          left: position.x,
          top: position.y,
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        onContextMenu={handleRightClick}
      >
        <div className="mb-0.5 transition-colors">
          <IconComponent size={DESKTOP_ICON_SIZE} className="desktop-icon-color" />
        </div>
        {icon.isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={handleRenameBlur}
            className={renameInputClasses}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`text-xs text-center leading-tight px-2 py-1 rounded-md max-w-full ${
              isSelected
                ? selectedLabelClasses
                : 'desktop-icon-text-color desktop-icon-bg'
            }`}
          >
            {icon.label}
          </span>
        )}
      </div>

      {/* Context Menu */}
      <ContextMenu
        items={contextMenu.items}
        x={contextMenu.x}
        y={contextMenu.y}
        isVisible={contextMenu.isVisible}
        onClose={hideContextMenu}
      />
    </>
  );
};